import { useConfirmDialog, useError, useMutation } from '@ztechapps/common.hook'
import { orderFragment } from '../graphql'
import type { Customer, Order, UpdateOrCreateOrderByCustomerArgs } from '@ztechapps/common.contract'
import type { ConfirmDialogOptions } from '@ztechapps/common.hook'
import type { PartialDeep } from 'type-fest'
import { gql } from '@apollo/client'
import type { SelectCreatableOption } from '@ztechapps/webui.input'

const updateOrCreateOrderCustomerQuery = gql`mutation updateOrCreateOrderCustomer($input: UpdateOrCreateOrderByCustomerArgs!) {
  updateOrCreateOrderCustomer(input: $input) {
      _id
    ${orderFragment}
  }
}`

export type OnChangeCustomerInput = PartialDeep<Customer> & { company?: SelectCreatableOption; __isCreate?: boolean }

export const useUpdateOrCreateOrderCustomer = () => {
  const [onUpdateOrderCustomer] = useMutation({ query: updateOrCreateOrderCustomerQuery })
  const { onError } = useError()

  const onChangeCustomer = async ({ by, orderId }: { by: OnChangeCustomerInput; orderId?: string }): Promise<Order> => {
    try {
      const input = {
        orderId,
        name: by?.profile?.name,
        customerId: by?._id,
        email: by?.profile?.email,
      } as UpdateOrCreateOrderByCustomerArgs & { _id: string }

      //need to set the customerId to null if it is a new customer so it will create a new one from server
      if (by?.__isCreate) {
        input.customerId = null
      }

      //set the companyName and skipDomain if it is a new company
      if (by?.company?.__isCreate) {
        input.companyName = by?.company?.value
        input.skipDomain = true
      } else {
        input.companyId = by?.company?._id
      }

      const { data, errors } = await onUpdateOrderCustomer({ input })
      if (errors) throw errors

      const order: Order = data?.updateOrCreateOrderCustomer
      return order
    } catch (e) {
      onError(e)
    }
  }

  const { confirm } = useConfirmDialog({
    text: `Are you sure you want to change this order's customer?`,
    title: 'Change customer?',
    buttons: { confirm: { text: 'Yes, change it!' } },
  })

  const onChangeCustomerWithConfirm = async ({
    by,
    config,
    orderId,
  }: {
    by: OnChangeCustomerInput
    config?: Partial<ConfirmDialogOptions>
    orderId?: string
  }) => {
    await confirm(
      async () => {
        await onChangeCustomer({ by, orderId })
      },
      null,
      config
    )
  }

  return { onChangeCustomer, onChangeCustomerWithConfirm }
}
